import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
    Check, 
    Navigation, 
    Clock, 
    ChevronDown, 
    ChevronUp, 
    MapPin, 
    Truck, 
    Settings,
    Info,
    Calendar
} from 'lucide-react';
import { getPropertyValue } from '../../../services/traceability-api';

const RouteMapView = ({ activeStep, setActiveStep, timelineSteps, isMobile }) => {
    const { t } = useTranslation();
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef([]);
    const [isWaypointsOpen, setIsWaypointsOpen] = useState(false);

    const waypoints = useMemo(() => timelineSteps.map((step, index) => {
        const [lat, lng] = String(step.location || '').split(',').map(value => Number(value.trim()));
        const valid = Number.isFinite(lat) && Number.isFinite(lng)
            && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
            && (lat !== 0 || lng !== 0);
        return valid ? { id: index, lat, lng, status: step.status } : null;
    }).filter(Boolean), [timelineSteps]);

    const totalDistance = useMemo(() => {
        const toRadians = value => value * Math.PI / 180;
        let distance = 0;
        for (let index = 1; index < waypoints.length; index += 1) {
            const previous = waypoints[index - 1];
            const current = waypoints[index];
            const latDelta = toRadians(current.lat - previous.lat);
            const lngDelta = toRadians(current.lng - previous.lng);
            const a = Math.sin(latDelta / 2) ** 2
                + Math.cos(toRadians(previous.lat)) * Math.cos(toRadians(current.lat))
                * Math.sin(lngDelta / 2) ** 2;
            distance += 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        }
        return waypoints.length > 1 ? `${distance.toFixed(1)} km` : '—';
    }, [waypoints]);

    const transitTime = useMemo(() => {
        const timestamps = timelineSteps
            .map(step => step.startTime || step.endTime)
            .filter(Boolean)
            .map(value => new Date(value).getTime())
            .filter(Number.isFinite);
        if (timestamps.length < 2) return '—';
        const minutes = Math.max(...timestamps) - Math.min(...timestamps);
        const totalMinutes = Math.round(minutes / 60000);
        return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
    }, [timelineSteps]);

    useEffect(() => {
        if (!mapInstance.current && mapRef.current) {
            mapInstance.current = L.map(mapRef.current, {
                center: waypoints.length > 0 ? [waypoints[0].lat, waypoints[0].lng] : [20.5937, 78.9629],
                zoom: waypoints.length > 0 ? 10 : 5,
                zoomControl: false,
                attributionControl: false
            });

            L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                maxZoom: 19,
            }).addTo(mapInstance.current);

            const routeCoords = waypoints.map(wp => [wp.lat, wp.lng]);
            if (routeCoords.length > 1) {
                L.polyline(routeCoords, {
                    color: '#02C170',
                    weight: 3,
                    dashArray: '10, 10',
                    opacity: 0.6
                }).addTo(mapInstance.current);
            }
        }

        if (mapInstance.current) {
            markersRef.current.forEach(m => m.remove());
            markersRef.current = [];

            waypoints.forEach((wp) => {
                const index = wp.id;
                const isActive = activeStep === index;
                const isCompleted = wp.status === 'completed';
                const isDisabled = timelineSteps[index]?.status === 'pending';

                const markerSize = isActive ? 40 : 32;
                const icon = L.divIcon({
                    className: 'custom-leaflet-marker',
                    html: `
                        <div class="marker-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isDisabled ? 'disabled' : ''}" style="width:${markerSize}px;height:${markerSize}px;">
                            ${index + 1}
                        </div>
                    `,
                    iconSize: [markerSize, markerSize],
                    iconAnchor: [markerSize / 2, markerSize / 2]
                });

                const marker = L.marker([wp.lat, wp.lng], { icon })
                    .addTo(mapInstance.current)
                    .on('click', () => {
                        if (!isDisabled) setActiveStep(index);
                    });

                markersRef.current.push(marker);
            });

            const activeWp = waypoints.find(waypoint => waypoint.id === activeStep);
            if (activeWp) {
                mapInstance.current.panTo([activeWp.lat, activeWp.lng], { animate: true });
            }

            // Invalidate size after render for mobile
            setTimeout(() => mapInstance.current?.invalidateSize(), 100);
        }
    }, [activeStep, setActiveStep, timelineSteps]);

    const currentStep = timelineSteps[activeStep];
    const isInProgress = currentStep?.status === 'in-progress';
    const currentTemperature = getPropertyValue(currentStep?.properties, 'Temperature') || '—';
    const currentCoordinates = currentStep?.location || '—';
    const currentDescription = currentStep
        ? `${currentStep.label} recorded`
        : '—';

    return (
        <div style={{ position: 'relative', height: isMobile ? 'calc(100vh - 190px)' : '670px', borderRadius: isMobile ? '0' : '16px', overflow: 'hidden', border: isMobile ? 'none' : '0.8px solid #E2E8F0', backgroundColor: '#F4F6F9' }}>
            {/* Map Container */}
            <div ref={mapRef} style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1 }}></div>

            {/* ── MOBILE LAYOUT ── */}
            {isMobile ? (
                <>
                    {/* Top: Stats Bar + Waypoints Toggle */}
                    <div style={{ position: 'absolute', top: '16px', left: '16px', right: '16px', zIndex: 1000, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Stats Bar */}
                        <div style={{ 
                            display: 'flex', gap: '24px', alignItems: 'center', 
                            backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '14px', 
                            padding: '12px 16px', border: '0.8px solid #E2E8F0',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                        }}>
                            <div>
                                <div style={{ fontSize: '10px', fontWeight: '500', color: '#90A1B9' }}>Total Distance</div>
                                <div style={{ fontSize: '16px', fontWeight: '700', color: '#071422' }}>{totalDistance}</div>
                            </div>
                            <div style={{ width: '1px', height: '32px', backgroundColor: '#E2E8F0' }}></div>
                            <div>
                                <div style={{ fontSize: '10px', fontWeight: '500', color: '#90A1B9' }}>Transit Time</div>
                                <div style={{ fontSize: '16px', fontWeight: '700', color: '#071422' }}>{transitTime}</div>
                            </div>
                            <div style={{ width: '1px', height: '32px', backgroundColor: '#E2E8F0' }}></div>
                            <div>
                                <div style={{ fontSize: '10px', fontWeight: '500', color: '#90A1B9' }}>Recorded Temp</div>
                                <div style={{ fontSize: '16px', fontWeight: '700', color: '#02C170' }}>{currentTemperature}</div>
                            </div>
                        </div>

                        {/* Waypoints Toggle Button */}
                        <button 
                            onClick={() => setIsWaypointsOpen(!isWaypointsOpen)}
                            style={{ 
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '12px 16px',
                                border: '0.8px solid #E2E8F0', boxShadow: '0 4px 18.8px rgba(0,0,0,0.08)',
                                cursor: 'pointer', width: '100%'
                            }}
                        >
                            <div style={{ textAlign: 'left' }}>
                                <div style={{ fontSize: '14px', fontWeight: '700', color: '#071422' }}>Route Waypoints</div>
                                <div style={{ fontSize: '11px', color: '#90A1B9' }}>Select a waypoint to view the recorded map point</div>
                            </div>
                            <ChevronDown size={16} color="#62748E" style={{ transform: isWaypointsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
                        </button>

                        {/* Expandable Waypoints List */}
                        {isWaypointsOpen && (
                            <div style={{ 
                                backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '12px', 
                                border: '0.8px solid #E2E8F0', boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                                maxHeight: '300px', overflowY: 'auto'
                            }}>
                                {timelineSteps.map((step, index) => {
                                    const isActive = activeStep === index;
                                    const isDisabled = step.status === 'pending';
                                    const isCompleted = step.status === 'completed';

                                    return (
                                        <div 
                                            key={index}
                                            onClick={() => { if (!isDisabled) { setActiveStep(index); setIsWaypointsOpen(false); } }}
                                            style={{ 
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                                padding: '10px 16px', cursor: isDisabled ? 'default' : 'pointer',
                                                backgroundColor: isActive ? '#071422' : 'transparent',
                                                borderBottom: index < timelineSteps.length - 1 ? '1px solid #F1F5F9' : 'none',
                                                opacity: isDisabled ? 0.5 : 1
                                            }}
                                        >
                                            <div style={{ 
                                                width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                                                backgroundColor: isCompleted ? '#02C170' : '#E2E8F0',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '11px', fontWeight: '700', color: isCompleted ? '#FFF' : '#CAD5E2'
                                            }}>
                                                {index + 1}
                                            </div>
                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                <div style={{ fontSize: '12.5px', fontWeight: '600', color: isActive ? '#FFF' : isDisabled ? '#CAD5E2' : '#071422' }}>{step.label}</div>
                                                <div style={{ fontSize: '11px', fontWeight: '500', color: isActive ? 'rgba(255,255,255,0.5)' : '#90A1B9' }}>
                                                    {step.date !== '—' ? step.date : '—'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Bottom: Active Step Info Card */}
                    <div style={{ 
                        position: 'absolute', bottom: '16px', left: '16px', right: '16px', zIndex: 1000,
                        backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '16px',
                        border: '0.8px solid #E2E8F0', boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                        padding: '14px'
                    }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <div style={{ 
                                width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
                                backgroundColor: 'rgba(2,193,112,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                {activeStep === 0 && <Info size={16} color="#02C170" />}
                                {activeStep === 1 && <Truck size={16} color="#02C170" />}
                                {activeStep === 2 && <Calendar size={16} color="#02C170" />}
                                {activeStep === 3 && <Settings size={16} color="#02C170" />}
                                {activeStep === 4 && <Check size={16} color="#02C170" />}
                                {activeStep === 5 && <Check size={16} color="#02C170" />}
                            </div>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1px' }}>
                                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#071422' }}>{currentStep?.label}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '7px' }}>
                                    <Navigation size={14} color="#90A1B9" />
                                    <span style={{ fontSize: '11px', fontWeight: '500', color: '#90A1B9' }}>
                                        {currentCoordinates}
                                    </span>
                                </div>
                                <div style={{ fontSize: '13px', color: '#62748E', marginBottom: '4px' }}>
                                    {currentDescription}
                                </div>
                                <div style={{ display: 'flex', gap: '26px', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <MapPin size={12} color="#90A1B9" />
                                        <span style={{ fontSize: '11.5px', color: '#90A1B9' }}>{currentStep?.location}</span>
                                    </div>
                                    {currentStep?.date !== '—' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Clock size={12} color="#90A1B9" />
                                            <span style={{ fontSize: '11.5px', color: '#90A1B9' }}>{currentStep?.date}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                /* ── DESKTOP LAYOUT ── */
                <>
                    {/* Left: Waypoints List */}
                    <div style={{ 
                        position: 'absolute', top: '16px', left: '16px', width: '280px', zIndex: 1000,
                        backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '16px', 
                        border: '0.8px solid #E2E8F0', boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                        overflow: 'hidden', maxHeight: 'calc(100% - 32px)', display: 'flex', flexDirection: 'column'
                    }}>
                        <div style={{ padding: '16px', borderBottom: '0.8px solid #E2E8F0' }}>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#071422' }}>Route Waypoints</div>
                            <div style={{ fontSize: '11px', color: '#90A1B9' }}>Select a waypoint to view the recorded map point</div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', paddingTop: '4px' }}>
                            {timelineSteps.map((step, index) => {
                                const isActive = activeStep === index;
                                const isDisabled = step.status === 'pending';
                                const isCompleted = step.status === 'completed';
                                const stepIsInProgress = step.status === 'in-progress';

                                return (
                                    <div 
                                        key={index}
                                        onClick={() => !isDisabled && setActiveStep(index)}
                                        style={{ 
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            padding: '10px 16px', height: '52px', cursor: isDisabled ? 'default' : 'pointer',
                                            backgroundColor: isActive ? '#071422' : 'transparent',
                                            opacity: isDisabled ? 0.5 : 1, transition: 'background-color 0.15s ease'
                                        }}
                                    >
                                        <div style={{ 
                                            width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                                            backgroundColor: isCompleted ? '#02C170' : '#E2E8F0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '11px', fontWeight: '700', color: isCompleted ? '#FFF' : '#CAD5E2'
                                        }}>
                                            {index + 1}
                                        </div>
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <div style={{ fontSize: '12.5px', fontWeight: '600', color: isActive ? '#FFF' : isDisabled ? '#CAD5E2' : '#071422', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{step.label}</div>
                                            <div style={{ fontSize: '11px', fontWeight: '500', color: isActive ? 'rgba(255,255,255,0.5)' : '#90A1B9' }}>
                                                {step.date !== '—' ? step.date : '—'}
                                            </div>
                                        </div>
                                        {stepIsInProgress && (
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#02C170', opacity: 0.96 }}></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Top Right: Stats Bar */}
                    <div style={{ 
                        position: 'absolute', top: '16px', right: '16px', zIndex: 1000,
                        display: 'flex', gap: '16px', alignItems: 'center',
                        backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '14px',
                        padding: '12px 16px', border: '0.8px solid #E2E8F0',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                    }}>
                        <div>
                            <div style={{ fontSize: '10px', fontWeight: '500', color: '#90A1B9' }}>Total Distance</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#071422' }}>{totalDistance}</div>
                        </div>
                        <div style={{ width: '1px', height: '32px', backgroundColor: '#E2E8F0' }}></div>
                        <div>
                            <div style={{ fontSize: '10px', fontWeight: '500', color: '#90A1B9' }}>Transit Time</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#071422' }}>{transitTime}</div>
                        </div>
                        <div style={{ width: '1px', height: '32px', backgroundColor: '#E2E8F0' }}></div>
                        <div>
                            <div style={{ fontSize: '10px', fontWeight: '500', color: '#90A1B9' }}>Current Temp</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#02C170' }}>{currentTemperature}</div>
                        </div>
                    </div>

                    {/* Bottom Center: Active Step Info */}
                    <div style={{ 
                        position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)', 
                        width: '520px', maxWidth: 'calc(100% - 340px)', zIndex: 1000,
                        backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '16px',
                        border: '0.8px solid #E2E8F0', boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                        padding: '20px 24px'
                    }}>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ 
                                width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
                                backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                {activeStep === 0 && <Info size={16} color="#02C170" />}
                                {activeStep === 1 && <Truck size={16} color="#02C170" />}
                                {activeStep === 2 && <Calendar size={16} color="#02C170" />}
                                {activeStep === 3 && <Settings size={16} color="#02C170" />}
                                {activeStep === 4 && <Check size={16} color="#02C170" />}
                                {activeStep === 5 && <Check size={16} color="#02C170" />}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0px' }}>
                                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#071422' }}>{currentStep?.label}</span>
                                </div>
                                <div style={{ fontSize: '13px', color: '#62748E', marginBottom: '4px' }}>
                                    {currentDescription}
                                </div>
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <MapPin size={12} color="#90A1B9" />
                                        <span style={{ fontSize: '11.5px', color: '#90A1B9' }}>{currentStep?.location}</span>
                                    </div>
                                    {currentStep?.date !== '—' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Clock size={12} color="#90A1B9" />
                                            <span style={{ fontSize: '11.5px', color: '#90A1B9' }}>{currentStep?.date}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                <Navigation size={14} color="#90A1B9" />
                                <span style={{ fontSize: '11px', fontWeight: '500', color: '#90A1B9' }}>
                                    {currentCoordinates}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Zoom Controls */}
                    <div style={{ 
                        position: 'absolute', bottom: '24px', right: '24px', zIndex: 1000,
                        border: '1.6px solid rgba(0,0,0,0.2)', borderRadius: '4px', overflow: 'hidden'
                    }}>
                        <button onClick={() => mapInstance.current?.zoomIn()} style={{ 
                            display: 'block', width: '30px', height: '30px', backgroundColor: '#FFF', border: 'none',
                            borderBottom: '0.8px solid #CCC', cursor: 'pointer', fontSize: '18px', fontWeight: '700'
                        }}>+</button>
                        <button onClick={() => mapInstance.current?.zoomOut()} style={{ 
                            display: 'block', width: '30px', height: '30px', backgroundColor: '#FFF', border: 'none',
                            cursor: 'pointer', fontSize: '18px', fontWeight: '700'
                        }}>−</button>
                    </div>
                </>
            )}

            <style dangerouslySetInnerHTML={{ __html: `
                .marker-dot {
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 700;
                    color: #CAD5E2;
                    background-color: #E2E8F0;
                    border: 2px solid white;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.25);
                    transition: all 0.2s ease;
                    font-family: 'Poppins', sans-serif;
                }
                .marker-dot.completed, .marker-dot.in-progress {
                    background-color: #02C170;
                    color: white;
                    border-color: white;
                }
                .marker-dot.in-progress {
                    border-color: rgba(2,193,112,0.1);
                    box-shadow: 0 2px 12px rgba(223,165,42,0.4);
                }
                .marker-dot.active {
                    transform: scale(1.15);
                    box-shadow: 0 2px 12px rgba(0,0,0,0.35);
                }
                .marker-dot.disabled {
                    opacity: 0.4;
                }
                .custom-leaflet-marker {
                    background: none !important;
                    border: none !important;
                }
            `}} />
        </div>
    );
};

export default RouteMapView;
